/**
 * Program 12.1 UX4 — Executive bid decision surface (extends UX2 cockpit).
 */
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  List,
  Progress,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_EPC_BID_DECISION } from '@/constants/approval-entity-types';
import { epcCommercialService } from '@/services/epcCommercial.service';
import {
  bidReadinessTagColor,
  buildBidDecisionReadiness,
  type BidReadinessState,
} from './bidDecisionReadiness';
import {
  computeOverallScore,
  dimensionAssessmentPatch,
  hasDeterministicOverallScore,
  mergeFactorsPatch,
  outcomeDisplayLabel,
  parseBidDimensions,
  parseConditionalConditions,
  qualitativeRecommendation,
} from './bidDecisionDimensions';
import { formatDeadlineDisplay } from './bidWorkspaceModel';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const OUTCOME_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'GO', label: 'GO (Bid)' },
  { value: 'NO_GO', label: 'NO_GO (No-Bid)' },
  { value: 'CONDITIONAL', label: 'Conditional Bid' },
];

const ASSESSMENT_OPTIONS = ['Low', 'Medium', 'High', 'Unknown'].map((v) => ({
  value: v,
  label: v,
}));

export type EpcBidDecisionCockpitProps = {
  row: Record<string, unknown>;
  tender?: Record<string, unknown> | null;
  onUpdated?: () => void;
  compact?: boolean;
};

export const EpcBidDecisionCockpit: React.FC<EpcBidDecisionCockpitProps> = ({
  row,
  tender,
  onUpdated,
  compact = false,
}) => {
  const qc = useQueryClient();
  const id = String(row.id || '');
  const tenderId = String(row.tender_id || row.tenderId || tender?.id || '');
  const status = String(row.status || 'Draft');
  const outcome = String(row.outcome || 'PENDING');
  const editable = !['Submitted', 'Approved'].includes(status);
  const isNoBidApproved = status === 'Approved' && outcome.toUpperCase() === 'NO_GO';

  const readiness = useMemo(() => buildBidDecisionReadiness(row), [row]);
  const dimensions = useMemo(
    () => parseBidDimensions(row.factors_json || row.factorsJson),
    [row]
  );
  const conditions = useMemo(
    () => parseConditionalConditions(row.factors_json || row.factorsJson),
    [row]
  );
  const overallScore = useMemo(() => computeOverallScore(dimensions), [dimensions]);
  const showNumericScore = hasDeterministicOverallScore(dimensions) && overallScore;

  const patchM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcCommercialService.patchBidDecision(id, payload),
    onSuccess: () => {
      message.success('Bid decision updated');
      qc.invalidateQueries({ queryKey: ['epc-bid-decisions'] });
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace'] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const submitM = useMutation({
    mutationFn: () => epcCommercialService.submitBidDecision(id),
    onSuccess: () => {
      message.success('Submitted for approval');
      qc.invalidateQueries({ queryKey: ['epc-bid-decisions'] });
      qc.invalidateQueries({ queryKey: ['approval-summary', ENTITY_TYPE_EPC_BID_DECISION, id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Submit failed'),
  });

  const patchDimension = (key: string, value: string) => {
    const next = mergeFactorsPatch(row.factors_json || row.factorsJson, dimensionAssessmentPatch(key, value));
    patchM.mutate({ factorsJson: next });
  };

  if (isNoBidApproved) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card>
          <Title level={4} style={{ marginTop: 0, color: '#cf1322' }}>
            NO-BID
          </Title>
          <Paragraph>{String(row.notes || 'No rationale recorded')}</Paragraph>
          <Text type="secondary">
            Approved {row.decided_at ? formatDeadlineDisplay(row.decided_at) : '—'}
          </Text>
        </Card>
        <Alert
          type="info"
          showIcon
          message="Pursuit closed for bidding"
          description="Estimate, proposal, and submission preparation are not required. Review outcome and lessons in history."
        />
      </Space>
    );
  }

  const hero = tender && !compact && (
    <Card style={{ marginBottom: 16 }}>
      <Title level={4} style={{ marginTop: 0 }}>
        BID / NO-BID DECISION
      </Title>
      <Row gutter={[16, 8]}>
        <Col xs={12} md={6}>
          <Text type="secondary">Client</Text>
          <div>{String(tender.client_name || '—')}</div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Submission due</Text>
          <div>{formatDeadlineDisplay(tender.submission_due_at || tender.submissionDueAt)}</div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Currency</Text>
          <div>{String(tender.currency || 'AED')}</div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Decision readiness</Text>
          <Progress
            percent={
              readiness.checks.filter((c) => c.state === 'READY').length > 0
                ? Math.round(
                    (readiness.checks.filter((c) => c.state === 'READY').length /
                      readiness.checks.length) *
                      100
                  )
                : 0
            }
            size="small"
          />
        </Col>
      </Row>
      {qualitativeRecommendation(outcome) && (
        <Tag color="blue" style={{ marginTop: 12 }}>
          {qualitativeRecommendation(outcome)}
          {showNumericScore
            ? ` · Score ${overallScore!.total}/${overallScore!.max}`
            : ' · Confidence: review dimensions'}
        </Tag>
      )}
    </Card>
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {hero}
      <div>
        <Space wrap style={{ marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>
            {String(row.decision_number || 'Bid decision')}
          </Title>
          <Tag color={bidReadinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
          <Tag>{status}</Tag>
          <Tag>{outcomeDisplayLabel(outcome)}</Tag>
        </Space>
      </div>

      {!compact && (
        <Card size="small" title="Decision dimensions (human assessment)">
          <Table
            size="middle"
            pagination={false}
            rowKey="key"
            dataSource={dimensions}
            scroll={{ x: 640 }}
            columns={[
              { title: 'Dimension', dataIndex: 'label', width: 220 },
              {
                title: 'Kind',
                dataIndex: 'kind',
                width: 160,
                render: (k: string) => <Tag>{k.replace('_', ' ')}</Tag>,
              },
              {
                title: 'Assessment',
                key: 'value',
                width: 180,
                render: (_: unknown, r) =>
                  editable ? (
                    <Select
                      size="middle"
                      style={{ width: '100%', minWidth: 140 }}
                      value={r.value || undefined}
                      placeholder="Set"
                      options={ASSESSMENT_OPTIONS}
                      onChange={(v) => patchDimension(r.key, v)}
                      disabled={patchM.isPending}
                    />
                  ) : (
                    r.value || '—'
                  ),
              },
              {
                title: 'Score',
                key: 'score',
                width: 88,
                align: 'right' as const,
                render: (_: unknown, r) =>
                  r.score != null && r.maxScore != null ? `${r.score}/${r.maxScore}` : '—',
              },
            ]}
          />
          {!showNumericScore && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              No fabricated overall score — qualitative recommendation from outcome and dimensions.
            </Text>
          )}
        </Card>
      )}

      {outcome.toUpperCase() === 'CONDITIONAL' && conditions.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Conditional bid — attention items"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {conditions.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          }
        />
      )}

      <Card size="small" title="Readiness">
        <List
          size="small"
          dataSource={readiness.groups}
          renderItem={(g) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{g.name}</Text>
                    <Tag color={bidReadinessTagColor(g.state as BidReadinessState)}>{g.state}</Tag>
                  </Space>
                }
                description={
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {g.checks.map((c) => (
                      <li key={c.id}>
                        {c.label}: {c.state}
                        {c.detail ? ` — ${c.detail}` : ''}
                      </li>
                    ))}
                  </ul>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Card size="small" title="Decision (domain API)">
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <div>
            <Text type="secondary">Outcome</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              disabled={!editable || patchM.isPending}
              value={outcome}
              options={OUTCOME_OPTIONS}
              onChange={(o) => patchM.mutate({ outcome: o })}
              aria-label="Bid outcome"
            />
          </div>
          <div>
            <Text type="secondary">Notes / rationale</Text>
            <TextArea
              key={`${id}-${String(row.notes || '')}`}
              defaultValue={String(row.notes || '')}
              disabled={!editable || patchM.isPending}
              rows={3}
              style={{ marginTop: 4 }}
              onBlur={(e) => {
                const notes = e.target.value;
                if (notes !== String(row.notes || '')) patchM.mutate({ notes });
              }}
              aria-label="Bid rationale"
            />
          </div>
          <Button
            type="primary"
            disabled={!readiness.canSubmit}
            loading={submitM.isPending}
            title={readiness.submitBlockedReason}
            onClick={() => submitM.mutate()}
          >
            Submit Bid Decision
          </Button>
          {!readiness.canSubmit && readiness.submitBlockedReason ? (
            <Alert type="info" showIcon message={readiness.submitBlockedReason} />
          ) : null}
        </Space>
      </Card>

      <Card size="small" title="Approval">
        {id ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_EPC_BID_DECISION}
            entityId={id}
            extraInvalidateQueryKeys={[['epc-bid-decisions'], ['epc-tender-workspace']]}
          />
        ) : (
          <Alert type="warning" message="Missing bid decision id" />
        )}
      </Card>
    </Space>
  );
};

export default EpcBidDecisionCockpit;
